import { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { GetNotificationsResponse } from '../api/models';
import { useAuth } from '../auth/AuthContext';
import { ApiHealth } from '../hooks/useApiHealth';
import { findActiveNav, NavItem, navSections, NavSection } from './Sidebar.nav';
import { SidebarStatus } from './SidebarStatus';

interface SidebarProps {
  health: ApiHealth;
}

function badgeValue(item: NavItem, notifications: GetNotificationsResponse | null): number | null {
  if (!notifications) return null;
  let total = 0;
  if (item.badgeKey) {
    const v = notifications[item.badgeKey];
    if (typeof v === 'number') total += v;
  }
  if (item.children) {
    for (const child of item.children) {
      const childVal = badgeValue(child, notifications);
      if (childVal != null) total += childVal;
    }
  }
  return total > 0 ? total : null;
}

function filterNavByRole(sections: NavSection[], isAdmin: boolean): NavSection[] {
  const filterItems = (items: NavItem[]): NavItem[] =>
    items
      .filter((i) => !i.adminOnly || isAdmin)
      .map((i) => (i.children ? { ...i, children: filterItems(i.children) } : i));
  return sections
    .map((s) => ({ ...s, items: filterItems(s.items) }))
    .filter((s) => s.items.length > 0);
}

export function Sidebar({ health }: SidebarProps) {
  const location = useLocation();
  const { isAdmin } = useAuth();
  const sections = useMemo(() => filterNavByRole(navSections, isAdmin), [isAdmin]);
  const active = findActiveNav(location.pathname);

  return (
    <aside className="w-[230px] bg-panel border-r border-border flex flex-col flex-shrink-0 h-full">
      {/* Brand */}
      <div className="h-[52px] px-5 flex items-center gap-2.5 border-b border-border flex-shrink-0">
        <div className="w-[24px] h-[24px] rounded-[6px] bg-gradient-to-br from-[#5b5bd6] to-[#8b5cf6] flex items-center justify-center text-white font-bold text-[12px]">
          P
        </div>
        <div className="font-semibold text-[14px] tracking-tight text-text">Parity</div>
      </div>

      {/* Nav */}
      <nav className="px-2 py-2.5 flex-1 overflow-auto">
        {sections.map((section, idx) => (
          <div key={section.title ?? `section-${idx}`}>
            {section.title ? (
              <div className="text-[10px] uppercase tracking-wider text-text-3 px-3 pt-3 pb-1 font-semibold">
                {section.title}
              </div>
            ) : (
              <div className="pt-1" />
            )}
            {section.items.map((item) => {
              const isActive = active.item?.key === item.key || active.parent?.key === item.key;
              const badge = badgeValue(item, health.notifications);
              const showChildren = item.children && (active.parent?.key === item.key || active.item?.key === item.key);
              return (
                <div key={item.key}>
                  <NavLink item={item} isActive={isActive} badge={badge} />
                  {showChildren && item.children && (
                    <div className="ml-3 mt-0.5 mb-1 border-l border-border pl-2">
                      {item.children.map((child) => {
                        const childActive = active.item?.key === child.key;
                        const childBadge = badgeValue(child, health.notifications);
                        return (
                          <NavLink key={child.key} item={child} isActive={childActive} badge={childBadge} compact />
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </nav>

      <SidebarStatus health={health} />
    </aside>
  );
}

interface NavLinkProps {
  item: NavItem;
  isActive: boolean;
  badge: number | null;
  compact?: boolean;
}

function NavLink({ item, isActive, badge, compact }: NavLinkProps) {
  const base = 'flex items-center gap-2.5 rounded-md font-medium cursor-pointer my-px text-[13px] no-underline';
  const padding = compact ? 'px-3 py-1' : 'px-3 py-1.5';
  const colors = isActive
    ? 'bg-accent-soft text-accent'
    : 'text-text-2 hover:bg-bg-elevated hover:text-text';
  return (
    <Link to={item.to} className={`${base} ${padding} ${colors}`}>
      <span className="w-3.5 opacity-70 text-center">{item.icon}</span>
      <span className="truncate">{item.label}</span>
      {badge != null && (
        <span
          className={`ml-auto font-mono text-[10px] px-1.5 py-px rounded-full font-semibold ${
            isActive ? 'bg-accent text-white' : 'bg-bg-elevated text-text-2'
          }`}
        >
          {badge}
        </span>
      )}
    </Link>
  );
}
